import styled, { css } from 'styled-components'

import FormLabel from '../../FormLabel'

type InputStyleProps = {
  $isError?: boolean
  $isActive?: boolean
  $hasLabel?: boolean
  $hasFloatingLabel?: boolean
  $isDisabled?: boolean
  $hasErrorMessage?: boolean
  $size?: 'default' | 'small'
}

export const Container = styled.div<InputStyleProps>`
  display: flex;
  flex-flow: column;

  // disabled
  ${({ $isDisabled }) =>
    $isDisabled === true &&
    css`
      opacity: 0.8;
      &,
      & * {
        cursor: not-allowed;
      }
    `}
`

export const Text = styled.p`
  ${({ theme }) => theme.textStyles.firaSansTextBase};
  color: ${({ theme }) => theme.colors.blueSecondaryBis};
  margin-bottom: 1.2rem;
`

export const Wrapper = styled.div``

export const InputContainer = styled.div<InputStyleProps>`
  border: 1px solid ${({ theme }) => theme.colors.greyBorder};
  background-color: ${({ theme }) => theme.colors.pureWhite};
  border-radius: 0.6rem;
  position: relative;
  padding: 1.8rem 2rem;
  width: 100%;

  ${({ $isError, theme }) =>
    $isError === true &&
    css`
      border-color: ${theme.colors.error};
    `}

  ${({ $isActive }) =>
    $isActive === true &&
    css`
      border-color: ${({ theme }) => theme.colors.bluePrimary};
      background-color: ${({ theme }) => theme.colors.blueLight};
    `}

  ${({ $isDisabled }) =>
    $isDisabled === true &&
    css`
      border-color: ${({ theme }) => theme.colors.greyContour};
      background-color: ${({ theme }) => theme.colors.pureWhite};
    `}
`

export const Label = styled(FormLabel)<{
  $active?: boolean
  $hasFloatingLabel?: boolean
}>`
  margin-bottom: 1.5rem;
  transition: all 0.3s ease;

  ${({ $active }) =>
    $active === true &&
    css`
      color: ${({ theme }) => theme.colors.bluePrimary};
    `}

  ${(props) =>
    props.$hasFloatingLabel &&
    css`
      position: absolute;
      transition: top 0.2s, font-size 0.2s, transform 0.2s, font-weight 0.2s;
      top: 1rem;
      margin: auto;
    `}
`

export const InputWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 1rem;
  position: relative;
`

export const inputStyle = css<InputStyleProps>`
  ${({ theme }) => theme.textStyles.montserratSm};
  line-height: 1;
  width: 100%;
  border: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.dustyGray};
  background: transparent;
  min-height: ${({ $size }) => ($size === 'default' ? '17.5rem' : '10rem')};
  resize: none;

  // disabled
  ${({ $isDisabled }) =>
    $isDisabled === true &&
    css`
      pointer-events: none;
    `}

  ${({ $isActive }) =>
    $isActive === true &&
    css`
      color: ${({ theme }) => theme.colors.bluePrimary};

      &::placeholder {
        color: ${({ theme }) => theme.colors.bluePrimary};
      }
    `}

  ${({ $hasLabel, $hasFloatingLabel, $isActive }) =>
    $hasLabel &&
    $hasFloatingLabel &&
    css`
      position: relative;
      transition: top 0.2s;
      top: 0.5rem;
      ${$isActive &&
      css`
        top: 1rem;
      `}
    `}

  // readonly
  &[readonly] {
    cursor: default;
  }

  &:focus {
    outline: none;
  }

  &[type='password'] {
    letter-spacing: 3px;
  }
`

export const Input = styled.textarea<InputStyleProps>`
  ${inputStyle}
`

export const HelpMessage = styled.p`
  ${({ theme }) => theme.textStyles.firaSansTextBase}
  color: ${({ theme }) => theme.colors.pureBlack};
  text-align: left;
`
export const ErrorMessage = styled.p`
  ${({ theme }) => theme.textStyles.firaSansTextBase}
  color: ${({ theme }) => theme.colors.error};
  text-align: left;
`
